<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { type CharacterActionLog } from '@/api/dx-backend';
import RPGContainer from '@/components/RPGContainer/RPGContainer.vue';
import RPGActionLogEntry from './RPGActionLogEntry.vue';
import {
  PHASE_ACTION,
  involvesCharacter,
  isCombatAction,
  netForCharacter,
  phaseOfAction,
  signedNumber
} from './actionLogShared';

const MAX_ENTRIES = 200;

type FilterId = 'all' | 'me' | 'fight';

const FILTERS: Array<{ id: FilterId, icon: string, key: string }> = [
  { id: 'all', icon: 'fa-bars', key: 'actionLog.filters.all' },
  { id: 'me', icon: 'fa-user', key: 'actionLog.filters.me' },
  { id: 'fight', icon: 'fa-khanda', key: 'actionLog.filters.fight' }
];

const props = withDefaults(defineProps<{
  open: boolean,
  actions: Array<CharacterActionLog>,
  selfCharacterId?: string | null,
  currentCycleNumber?: number | null
}>(), {
  actions: () => ([]),
  selfCharacterId: null,
  currentCycleNumber: null
});

const emit = defineEmits<{
  'close': [];
  'character-hover': [payload: { charId: string; event: MouseEvent }];
  'character-hover-leave': [];
  'character-selected': [charId: string];
}>();

const { t } = useI18n();

const activeFilter = ref<FilterId>('all');
const dialog = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);

const filteredActions = computed(() => {
  if (activeFilter.value === 'me') {
    return props.actions.filter((action) => involvesCharacter(action, props.selfCharacterId));
  }
  if (activeFilter.value === 'fight') return props.actions.filter(isCombatAction);
  return props.actions;
});

const orderedActions = computed(() =>
  [...filteredActions.value].sort((a, b) => {
    const cycleDiff = (b.cycle?.number ?? 0) - (a.cycle?.number ?? 0);
    if (cycleDiff !== 0) return cycleDiff;
    const phaseDiff = Number(phaseOfAction(b) === PHASE_ACTION) - Number(phaseOfAction(a) === PHASE_ACTION);
    if (phaseDiff !== 0) return phaseDiff;
    return (b.order ?? 0) - (a.order ?? 0);
  })
);

const visibleActions = computed(() => orderedActions.value.slice(0, MAX_ENTRIES));

const hiddenCount = computed(() =>
  Math.max(0, orderedActions.value.length - visibleActions.value.length)
);

const groups = computed(() => {
  const result: Array<{ key: string, cycleNumber: number, phase: string, actions: CharacterActionLog[] }> = [];
  visibleActions.value.forEach((action) => {
    const cycleNumber = action.cycle?.number ?? 0;
    const phase = phaseOfAction(action);
    const last = result[result.length - 1];
    if (last && last.cycleNumber === cycleNumber && last.phase === phase) {
      last.actions.push(action);
      return;
    }
    result.push({ key: `${cycleNumber}-${phase}-${result.length}`, cycleNumber, phase, actions: [action] });
  });
  return result;
});

const phaseLabel = (phase: string): string =>
  phase === PHASE_ACTION ? t('actionLog.phases.action') : t('actionLog.phases.preparation');

const groupNet = (actions: CharacterActionLog[]): number =>
  netForCharacter(actions, props.selfCharacterId);

const setFilter = (id: FilterId) => {
  activeFilter.value = id;
};

const close = () => emit('close');

const focusables = (): HTMLElement[] => {
  if (!dialog.value) return [];
  return Array.from(
    dialog.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element.offsetParent !== null);
};

const onKeydown = (event: KeyboardEvent) => {
  if (!props.open) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    close();
    return;
  }
  if (event.key !== 'Tab') return;
  const items = focusables();
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (event.shiftKey && (active === first || !dialog.value?.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

watch(() => props.open, (open) => {
  if (open) {
    window.addEventListener('keydown', onKeydown, true);
    nextTick(() => closeButton.value?.focus());
  } else {
    window.removeEventListener('keydown', onKeydown, true);
  }
}, { immediate: true });

watch(() => props.selfCharacterId, (id) => {
  if (!id && activeFilter.value === 'me') activeFilter.value = 'all';
});

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown, true));
</script>

<template>
  <Teleport to="body">
    <div
        v-if="open"
        class="log-modal-overlay"
        @click.self="close"
    >
      <RPGContainer class="log-modal">
        <div
            ref="dialog"
            class="log-modal-inner"
            role="dialog"
            aria-modal="true"
            :aria-label="t('actionLog.modal.title')"
        >
          <header class="log-head">
            <h2 class="log-title">{{ t('actionLog.modal.title') }}</h2>
            <span class="log-count">{{ t('actionLog.modal.entries', { count: orderedActions.length }) }}</span>

            <div class="log-filters" role="group" :aria-label="t('actionLog.modal.title')">
              <button
                  v-for="filter in FILTERS"
                  :key="filter.id"
                  type="button"
                  class="log-chip"
                  :class="{ 'is-active': activeFilter === filter.id }"
                  :disabled="filter.id === 'me' && !selfCharacterId"
                  :aria-pressed="activeFilter === filter.id"
                  @click="setFilter(filter.id)"
              >
                <i class="fa-solid" :class="filter.icon" aria-hidden="true"></i>
                <span>{{ t(filter.key) }}</span>
              </button>
            </div>

            <button
                ref="closeButton"
                type="button"
                class="log-close"
                :aria-label="t('actionLog.modal.close')"
                @click="close"
            ><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
          </header>

          <div class="log-body">
            <template v-if="groups.length">
              <section v-for="group in groups" :key="group.key" class="log-group">
                <header
                    class="log-group-head"
                    :class="{ 'is-current': group.cycleNumber === currentCycleNumber }"
                >
                  <span class="log-group-cycle">{{ group.cycleNumber }}</span>
                  <span class="log-group-phase">{{ phaseLabel(group.phase) }}</span>
                  <span
                      v-if="group.cycleNumber === currentCycleNumber"
                      class="log-group-now"
                  >{{ t('actionLog.currentMarker') }}</span>
                  <span class="log-group-fill"></span>
                  <span
                      v-if="groupNet(group.actions)"
                      class="log-group-net"
                      :class="groupNet(group.actions) < 0 ? 'is-negative' : 'is-positive'"
                      :title="t('actionLog.modal.net')"
                  >{{ signedNumber(groupNet(group.actions)) }}</span>
                  <span v-else class="log-group-net"></span>
                </header>

                <RPGActionLogEntry
                    v-for="action in group.actions"
                    :key="action.id"
                    :action="action"
                    :self-character-id="selfCharacterId"
                    @character-hover="emit('character-hover', $event)"
                    @character-hover-leave="emit('character-hover-leave')"
                    @character-selected="emit('character-selected', $event)"
                />
              </section>

              <p v-if="hiddenCount" class="log-truncated">
                {{ t('actionLog.olderHidden', { count: hiddenCount }) }}
              </p>
            </template>

            <p v-else class="log-empty">{{ t('actionLog.modal.empty') }}</p>
          </div>
        </div>
      </RPGContainer>
    </div>
  </Teleport>
</template>

<style scoped>
.log-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.82);
}

.log-modal {
  width: 100%;
  max-width: 920px;
  max-height: 92vh;
  flex-direction: column;
  overflow: hidden;
}

.log-modal-inner {
  --lg-ink: #e8eef5;
  --lg-dim: rgba(232, 238, 245, 0.42);
  --lg-gold: #fada95;
  --lg-self: #4ad9ff;
  --lg-dmg: #ff4d5e;
  --lg-heal: #4ade80;
  --lg-buff: #ffd147;
  --lg-debuff: #c77dff;
  --lg-rail: rgba(250, 218, 149, 0.18);
  --lg-value-col: 3.6rem;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  padding: 0.75rem 1rem 1rem;
  text-transform: none;
}

.log-head {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.6rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--lg-rail);
}

.log-title {
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--lg-gold);
}

.log-count {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  color: var(--lg-dim);
}

.log-filters {
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
}

.log-chip {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  height: 22px;
  padding: 0 0.5rem;
  border: 1px solid var(--lg-rail);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.35);
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--lg-dim);
  cursor: pointer;
  transition: color 0.12s ease, background-color 0.12s ease;
}

.log-chip i {
  font-size: 9px;
}

.log-chip:hover:not(:disabled) {
  color: var(--lg-ink);
}

.log-chip.is-active {
  color: #16130c;
  background: var(--lg-gold);
  border-color: var(--lg-gold);
}

.log-chip:disabled {
  opacity: 0.3;
  cursor: default;
}

.log-chip:focus-visible,
.log-close:focus-visible {
  outline: 1px solid var(--lg-gold);
  outline-offset: 1px;
}

.log-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--lg-rail);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.35);
  font-size: 11px;
  color: var(--lg-gold);
  cursor: pointer;
}

.log-close:hover {
  background: rgba(250, 218, 149, 0.16);
}

.log-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(250, 218, 149, 0.45) rgba(0, 0, 0, 0.4);
}

.log-group-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  padding: 0.35rem 0 0.2rem;
  background: rgba(10, 12, 16, 0.96);
  border-bottom: 1px solid var(--lg-rail);
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.log-group-cycle {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: var(--lg-gold);
}

.log-group-phase {
  color: var(--lg-dim);
}

.log-group-now {
  color: var(--lg-self);
}

.log-group-head.is-current {
  border-bottom-color: rgba(74, 217, 255, 0.45);
}

.log-group-fill {
  flex: 1 1 auto;
}

.log-group-net {
  flex: 0 0 var(--lg-value-col);
  padding-left: 0.5rem;
  border-left: 1px solid var(--lg-rail);
  text-align: right;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: var(--lg-dim);
}

.log-group-net.is-negative {
  color: var(--lg-dmg);
}

.log-group-net.is-positive {
  color: var(--lg-heal);
}

.log-truncated {
  margin: 0.5rem 0 0;
  font-family: 'Arial Narrow', 'Roboto Condensed', system-ui, sans-serif;
  font-size: 0.74rem;
  color: var(--lg-dim);
}

.log-empty {
  margin: 0;
  padding: 2.5rem 0.5rem;
  text-align: center;
  font-family: 'Arial Narrow', 'Roboto Condensed', system-ui, sans-serif;
  font-size: 0.84rem;
  color: var(--lg-dim);
}

@media (max-width: 640px) {
  .log-head {
    flex-wrap: wrap;
  }

  .log-chip span {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .log-chip {
    transition: none;
  }
}
</style>
